<template>
  <main class="main-content">
    <ToastPrime position="bottom-left" />
    
    <section class="login-section">
      <!-- Decorative background shapes -->
      <div class="bg-decoration">
        <div class="bg-shape bg-shape--1"></div>
        <div class="bg-shape bg-shape--2"></div>
        <div class="bg-shape bg-shape--3"></div>
      </div>

      <div class="login-container">
        <!-- Left: Hero Image Panel -->
        <div class="login-hero">
          <div class="hero-image-wrapper"
            :style="{
              backgroundImage: `url(${bgImageSrc})`,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundColor: bgIsFallback ? '#1a1f36' : 'transparent'
            }"
          >
            <div class="hero-overlay">
              <div class="hero-content">
                <!-- Brand -->
                <div class="hero-brand">
                  <div class="brand-icon">
                    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="40" height="40" rx="10" fill="white" fill-opacity="0.2"/>
                      <path d="M20 8L28 14V26L20 32L12 26V14L20 8Z" stroke="white" stroke-width="2" fill="white" fill-opacity="0.1"/>
                      <path d="M16 18L20 15L24 18V22L20 25L16 22V18Z" stroke="white" stroke-width="1.5" fill="white" fill-opacity="0.3"/>
                    </svg>
                  </div>
                  <div>
                    <h1 class="hero-title">E-Cataloguing</h1>
                    <p class="hero-subtitle">Tools</p>
                  </div>
                </div>

                <!-- Quote -->
                <blockquote class="hero-quote">
                  <p class="quote-text">"Streamlining your material cataloging process with precision and efficiency."</p>
                </blockquote>

                <!-- Company badges -->
                <div class="hero-badges">
                  <span v-for="company in ssoCompanies" :key="company.type" class="badge-item">
                    {{ company.label }}
                  </span>
                </div>
              </div>

              <!-- Footer credit -->
              <div class="hero-footer">
                <span class="hero-footer-text">SMARTCAT V4 &mdash; Intelligent Catalog Management</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Login Panel -->
        <div class="login-panel">
          <div class="login-card">
            <!-- Mobile brand -->
            <div class="mobile-brand d-lg-none text-center mb-4">
              <div class="mobile-brand-icon mb-2">
                <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" width="36" height="36">
                  <rect width="40" height="40" rx="10" fill="#5e72e4" fill-opacity="0.15"/>
                  <path d="M20 8L28 14V26L20 32L12 26V14L20 8Z" stroke="#5e72e4" stroke-width="2" fill="#5e72e4" fill-opacity="0.1"/>
                  <path d="M16 18L20 15L24 18V22L20 25L16 22V18Z" stroke="#5e72e4" stroke-width="1.5" fill="#5e72e4" fill-opacity="0.3"/>
                </svg>
              </div>
              <h4 class="fw-bold text-primary mb-0">E-Cataloguing Tools</h4>
              <p class="text-muted small">SMARTCAT V4</p>
            </div>

            <!-- Header -->                <div class="card-header-section">
              <div class="header-badge mb-2">
                <span class="badge-pill">
                  <i class="fas fa-shield-alt me-1"></i> Secure Portal
                </span>
              </div>
              <h3 class="card-title">Welcome Back</h3>
              <p class="card-subtitle">
                <template v-if="showCredential">Enter your credentials to continue</template>
                <template v-else>Select your company below to <span class="card-subtitle-highlight">LOG IN with SSO</span></template>
              </p>
            </div>

            <!-- Body -->
            <div class="card-body-section">
              <!-- SSO Login -->
              <template v-if="!showCredential">
                  <div class="sso-grid">
                    <button
                      v-for="company in ssoCompanies"
                      :key="company.type"
                      type="button"
                      class="sso-btn"
                      :class="[
                        `sso-btn--${company.type}`,
                        { 'is-loading': loadingType === company.type }
                      ]"
                      :disabled="loadingType !== ''"
                      @click="loginAuthAzure(company.type)"
                    >
                      <span class="sso-btn__state">
                        <i v-if="loadingType === company.type" class="fas fa-spinner fa-spin"></i>
                        <img v-else :src="company.logo" :alt="company.label" class="sso-btn__logo" />
                      </span>
                      <span class="sso-btn__label">{{ loadingType === company.type ? 'Connecting...' : company.label }}</span>
                    </button>
                  </div>

                  <div class="divider">
                    <span class="divider-text">or</span>
                  </div>

                  <div class="text-center">
                    <button class="btn-link-custom" @click.prevent="openCredential">
                      <i class="fas fa-key me-2"></i>Sign in with Username &amp; Password
                    </button>
                  </div>
              </template>

              <!-- Credential Login -->
              <template v-else>
                <LoginCredential ref="loginCredentialRef" />
                <div class="text-center mt-3">
                  <button class="btn-link-custom btn-link-custom--back" @click.prevent="showCredential = false">
                    <i class="fas fa-arrow-left me-2"></i>Back to company sign in
                  </button>
                </div>
              </template>
            </div>

            <!-- Footer -->
            <div class="card-footer-section">
              <p class="footer-text">
                &copy; {{ new Date().getFullYear() }} SMARTCAT V4. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import LoginCredential from "@/components/LoginCredential.vue";
import authService from "@/services/authService";
import baseApiUrl from "@/configs/config";

const body = document.getElementsByTagName("body")[0];
body.classList.remove("bg-gray-100");

export default {
  components: {
    LoginCredential,
  },
  data() {
    return {
      apiUrl: baseApiUrl.storageUrl,
      bgImageSrc: `${baseApiUrl.storageUrl}/gambar_login.jpg`,
      bgIsFallback: false,
      showCredential: false,
      loadingType: "",
      ssoCompanies: [
        { type: "mind-id", label: "MIND ID", logo: `${process.env.BASE_URL || "/"}mind-id.png` },
        { type: "inalum", label: "Inalum", logo: `${process.env.BASE_URL || "/"}inalum.png` },
        { type: "antam", label: "Antam", logo: `${process.env.BASE_URL || "/"}antam.png` },
        { type: "timah", label: "Timah", logo: `${process.env.BASE_URL || "/"}timah.png` },
      ],
    };
  },
  mounted() {
    this.$store.commit("argonConfig/toggleDefaultLayout");
    this.checkBgImage();

    const urlParams = new URLSearchParams(window.location.search);
    const resetStatus = urlParams.get('reset');

    if (resetStatus === 'success') {
      this.$toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Password has been reset successfully. You can now login with your new password.',
        life: 5000
      });

      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  },
  methods: {
    openCredential() {
      this.showCredential = true;
      this.$nextTick(() => {
        this.$refs.loginCredentialRef?.fetchCaptcha?.();
      });
    },
    checkBgImage() {
      const img = new Image();
      img.onerror = () => {
        this.bgImageSrc = `${process.env.BASE_URL || "/"}aerial-top-view-shot.jpg`;
        this.bgIsFallback = true;
      };
      img.src = this.bgImageSrc;
    },
    async loginAuthAzure(type) {
      this.loadingType = type;
      try {
        const response = await authService.loginWithSso(type);
        localStorage.setItem("ssoType", type);

        const redirect = this.$route.query.redirect;
        const isSafeRedirect = typeof redirect === "string" && redirect.startsWith("/") && !redirect.startsWith("//") && !redirect.includes("://");
        if (isSafeRedirect) {
          localStorage.setItem("ssoRedirect", redirect);
        } else {
          localStorage.removeItem("ssoRedirect");
        }

        window.location = response.data.data;
      } catch (error) {
        this.loadingType = "";
        const message = error.response?.data?.message || error.message || "Unknown error";
        alert("Login failed: " + message);
      }
    },
  },
};
</script>

<style scoped>
/* ============================================
   LOGIN SECTION – LAYOUT
   ============================================ */
.login-section {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f2f5;
  position: relative;
  overflow: hidden;
  padding: 1rem;
}

.login-container {
  display: flex;
  width: 100%;
  max-width: 1100px;
  height: 680px;
  border-radius: 2rem;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  position: relative;
  z-index: 1;
  background: #ffffff;
}

/* ============================================
   BACKGROUND DECORATION
   ============================================ */
.bg-decoration {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

.bg-shape {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.25;
}

.bg-shape--1 {
  width: 400px;
  height: 400px;
  background: #5e72e4;
  top: -150px;
  right: -100px;
}

.bg-shape--2 {
  width: 350px;
  height: 350px;
  background: #2dce89;
  bottom: -120px;
  left: -80px;
}

.bg-shape--3 {
  width: 250px;
  height: 250px;
  background: #fb6340;
  top: 50%;
  left: 30%;
  transform: translate(-50%, -50%);
}

/* ============================================
   LEFT HERO PANEL
   ============================================ */
.login-hero {
  flex: 0 0 50%;
  max-width: 50%;
  position: relative;
  display: flex;
}

.hero-image-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(30, 35, 65, 0.92) 0%,
    rgba(58, 70, 140, 0.75) 50%,
    rgba(94, 114, 228, 0.6) 100%
  );
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 3rem 2.5rem;
}

.hero-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Brand */
.hero-brand {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.brand-icon svg {
  width: 48px;
  height: 48px;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2));
}

.hero-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.5px;
}

.hero-subtitle {
  font-size: 0.95rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 3px;
}

/* Quote */
.hero-quote {
  margin: 0;
  padding: 0;
  border: none;
}

.quote-text {
  font-size: 1.1rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.8);
  font-style: italic;
  line-height: 1.6;
  margin: 0;
  max-width: 360px;
}

/* Badges */
.hero-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.badge-item {
  display: inline-block;
  padding: 0.35rem 0.85rem;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 100px;
}

/* Footer */
.hero-footer {
  padding-top: 1rem;
}

.hero-footer-text {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 1px;
  text-transform: uppercase;
}

/* ============================================
   RIGHT LOGIN PANEL
   ============================================ */
.login-panel {
  flex: 0 0 50%;
  max-width: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #ffffff;
}

.login-card {
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.login-card {
  width: 100%;
  max-width: 420px;
}

/* Header Section */
.card-header-section {
  margin-bottom: 1rem;
}

.header-badge {
  display: flex;
}

.badge-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.85rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #5e72e4;
  background: rgba(94, 114, 228, 0.08);
  border: 1px solid rgba(94, 114, 228, 0.15);
  border-radius: 100px;
}

.card-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: #32325d;
  margin: 0 0 0.2rem 0;
  letter-spacing: -0.3px;
}

.card-subtitle {
  font-size: 0.82rem;
  color: #32325d;
  margin: 0;
}

.card-subtitle-highlight {
  font-size: 0.82rem;
  font-weight: 800;
  color: #32325d;
  margin: 0;
}

/* Body Section */
.card-body-section {
  flex: 1;
  margin-bottom: 1.5rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* ============================================
   SSO BUTTONS
   ============================================ */
.sso-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.sso-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 1rem 0.75rem;
  min-height: 100px;
  background: #ffffff;
  border: 1.5px solid #e9ecef;
  border-radius: 1rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.sso-btn::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  opacity: 0;
  transition: opacity 0.25s ease;
}

.sso-btn:hover::before {
  opacity: 1;
}

.sso-btn:active {
  transform: scale(0.97);
}

.sso-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

/* Individual company hover colors */
.sso-btn--mind-id {
  border-color: #e9ecef;
}
.sso-btn--mind-id:hover {
  border-color: #5e72e4;
  box-shadow: 0 8px 25px rgba(94, 114, 228, 0.2);
  transform: translateY(-3px);
}

.sso-btn--inalum {
  border-color: #e9ecef;
}
.sso-btn--inalum:hover {
  border-color: #2dce89;
  box-shadow: 0 8px 25px rgba(45, 206, 137, 0.2);
  transform: translateY(-3px);
}

.sso-btn--antam {
  border-color: #e9ecef;
}
.sso-btn--antam:hover {
  border-color: #fb6340;
  box-shadow: 0 8px 25px rgba(251, 99, 64, 0.2);
  transform: translateY(-3px);
}

.sso-btn--timah {
  border-color: #e9ecef;
}
.sso-btn--timah:hover {
  border-color: #11cdef;
  box-shadow: 0 8px 25px rgba(17, 205, 239, 0.2);
  transform: translateY(-3px);
}

.sso-btn__state {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sso-btn__state i {
  font-size: 1.25rem;
  color: #5e72e4;
}

.sso-btn__logo {
  width: 44px;
  height: 44px;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.sso-btn:hover .sso-btn__logo {
  transform: scale(1.1);
}

.sso-btn__label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #344767;
  transition: color 0.2s ease;
}

.sso-btn--mind-id:hover .sso-btn__label { color: #5e72e4; }
.sso-btn--inalum:hover .sso-btn__label { color: #2dce89; }
.sso-btn--antam:hover .sso-btn__label { color: #fb6340; }
.sso-btn--timah:hover .sso-btn__label { color: #11cdef; }

/* ============================================
   DIVIDER
   ============================================ */
.divider {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1.25rem 0;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e9ecef;
}

.divider-text {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #adb5bd;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

/* ============================================
   CUSTOM LINK BUTTONS
   ============================================ */
.btn-link-custom {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  font-size: 0.85rem;
  font-weight: 600;
  color: #5e72e4;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-link-custom:hover {
  color: #324cdd;
  transform: translateX(2px);
}

.btn-link-custom--back:hover {
  transform: translateX(-2px);
}

/* ============================================
   CARD FOOTER
   ============================================ */
.card-footer-section {
  text-align: center;
  padding-top: 0.5rem;
  border-top: 1px solid #f0f2f5;
}

.footer-text {
  font-size: 0.72rem;
  color: #adb5bd;
  margin: 0;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 991.98px) {
  .login-container {
    max-width: 480px;
    min-height: auto;
    border-radius: 1.5rem;
  }

  .login-hero {
    display: none;
  }

  .login-panel {
    flex: 0 0 100%;
    max-width: 100%;
    padding: 2.5rem 1.75rem;
  }

  .login-card {
    max-width: 100%;
  }

  .card-header-section {
    text-align: center;
  }

  .header-badge {
    justify-content: center;
  }
}

@media (max-width: 575.98px) {
  .login-section {
    padding: 0.5rem;
  }

  .login-container {
    border-radius: 1rem;
  }

  .login-panel {
    padding: 1.75rem 1.25rem;
  }

  .sso-grid {
    gap: 0.5rem;
  }

  .sso-btn {
    padding: 0.9rem 0.5rem;
  }

  .sso-btn__state {
    width: 36px;
    height: 36px;
  }

  .sso-btn__logo {
    width: 36px;
    height: 36px;
  }

  .card-title {
    font-size: 1.5rem;
  }
}

/* ============================================
   ANIMATIONS
   ============================================ */
.login-card {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Hover transition for badges */
.badge-item {
  transition: all 0.2s ease;
}

.badge-item:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
}
</style>
